import React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Flex from '../Flex';
import Icon from '../Icon';
import Typo from '../Typo';
import theme from '../theme';
import { media } from '../../utils/media';

const Wrapper = styled.div`
  flex-shrink: 0;

  ${media.lessThan('phablet')} {
    width: 100%;
  }
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.NEU_200};
  background: ${theme.colors.NEU_0};
  cursor: pointer;
  white-space: nowrap;
  font-family: ${theme.fonts.TEXT_FONT}, sans-serif;

  &:hover {
    border-color: ${theme.colors.NEU_300};
  }

  ${media.lessThan('phablet')} {
    width: 100%;
    justify-content: space-between;
  }
`;

const OptionList = styled(Flex)`
  min-width: 180px;
  padding: 4px 0;
`;

const OptionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.NEU_50};
  }

  &[data-active='true'] p {
    font-weight: 500;
  }
`;

const ButtonLabel = styled(Typo.SmallText)`
  ${media.lessThan('phablet')} {
    width: 100%;
  }
`;

const ProviderFilter = ({ providers, selectedProviderId, onSelect }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const selectedLabel = selectedProviderId ? providers.find((p) => p.username === selectedProviderId)?.display_name : t('coursioNetwork.allProviders');

  const handleSelect = (value) => {
    onSelect(value);
    setAnchorEl(null);
  };

  return (
    <Wrapper>
      <SelectorButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Icon.Storefront size='x-small' color={theme.colors.NEU_600} />
        <ButtonLabel>{selectedLabel}</ButtonLabel>
        <Icon.ExpandMore size='x-small' color={theme.colors.NEU_600} />
      </SelectorButton>

      {anchorEl && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: '12px', marginTop: '4px' } } }}
        >
          <OptionList direction='column'>
            <OptionItem data-active={selectedProviderId === null} onClick={() => handleSelect(null)}>
              <Typo.SmallText>{t('coursioNetwork.allProviders')}</Typo.SmallText>
              {selectedProviderId === null && <Icon.Check size='x-small' color={theme.colors.NEU_PRIMARY} />}
            </OptionItem>
            {providers.map((provider) => (
              <OptionItem data-active={selectedProviderId === provider.username} key={provider.username} onClick={() => handleSelect(provider.username)}>
                <Typo.SmallText>{provider.display_name}</Typo.SmallText>
                {selectedProviderId === provider.username && <Icon.Check size='x-small' color={theme.colors.NEU_PRIMARY} />}
              </OptionItem>
            ))}
          </OptionList>
        </Popover>
      )}
    </Wrapper>
  );
};

ProviderFilter.displayName = 'ProviderFilter';

export default ProviderFilter;
